import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Building2, Mail, Phone, MapPin, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import GoldButton from '../components/GoldButton';

export default function ConcesionariosManagement() {
  const [concesionarios, setConcesionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
    ciudad: '',
    activo: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchConcesionarios();
  }, []);

  const fetchConcesionarios = async () => {
    try {
      const response = await api.get('/concesionarios?limit=100');
      // La respuesta ahora tiene formato: { data: [...], total, skip, limit, has_more }
      const concesionariosData = response.data.data || response.data;
      setConcesionarios(concesionariosData);
    } catch (error) {
      toast.error('Error al cargar concesionarios');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (concesionario = null) => {
    if (concesionario) {
      setEditingId(concesionario.id);
      setFormData({
        nombre: concesionario.nombre,
        direccion: concesionario.direccion,
        telefono: concesionario.telefono,
        email: concesionario.email,
        ciudad: concesionario.ciudad || '',
        activo: concesionario.activo
      });
    } else {
      setEditingId(null);
      setFormData({
        nombre: '',
        direccion: '',
        telefono: '',
        email: '',
        ciudad: '',
        activo: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      nombre: '',
      direccion: '',
      telefono: '',
      email: '',
      ciudad: '',
      activo: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingId) {
        await api.put(`/concesionarios/${editingId}`, formData);
        toast.success('Concesionario actualizado exitosamente');
      } else {
        await api.post('/concesionarios', formData);
        toast.success('Concesionario creado exitosamente');
      }
      handleCloseModal();
      fetchConcesionarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar concesionario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este concesionario?')) {
      return;
    }

    try {
      await api.delete(`/concesionarios/${id}`);
      toast.success('Concesionario eliminado');
      fetchConcesionarios();
    } catch (error) {
      toast.error('Error al eliminar concesionario');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️ ADVERTENCIA: Esto eliminará TODOS los concesionarios. ¿Estás seguro?')) {
      return;
    }
    
    if (!window.confirm('Esta acción NO se puede deshacer. ¿Confirmas eliminar todos los concesionarios?')) {
      return;
    }

    try {
      const response = await api.delete('/concesionarios/bulk/all');
      toast.success(response.data.message);
      fetchConcesionarios();
    } catch (error) {
      toast.error('Error al eliminar concesionarios');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#F5A623] text-xl font-semibold">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-[#666666]">
          Gestiona los concesionarios asignados para la entrega de vehículos
        </p>
        <div className="flex gap-3">
          {concesionarios.length > 0 && (
            <button
              onClick={handleDeleteAll}
              className="px-4 py-2 bg-[#EF4444] text-white rounded-lg hover:bg-[#DC2626] transition-colors flex items-center gap-2 text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar Todos
            </button>
          )}
          <GoldButton
            onClick={() => handleOpenModal()}
            variant="primary"
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Agregar Concesionario
          </GoldButton>
        </div>
      </div>

      <div className="grid gap-4">
        {concesionarios.length === 0 ? (
          <div className="bg-[#2A2A2A] border border-white/10 rounded-lg p-12 text-center">
            <Building2 className="w-16 h-16 text-[#666666] mx-auto mb-4" />
            <p className="text-[#666666] text-lg">No hay concesionarios registrados</p>
            <p className="text-[#666666] text-sm mt-2">Agrega el primer concesionario para comenzar</p>
          </div>
        ) : (
          concesionarios.map((concesionario) => (
            <motion.div
              key={concesionario.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#2A2A2A] border border-white/10 rounded-lg p-6 hover:border-[#F5A623]/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Building2 className="w-6 h-6 text-[#F5A623]" />
                    <h3 className="text-xl text-white font-bold">{concesionario.nombre}</h3>
                    {concesionario.activo ? (
                      <span className="px-3 py-1 bg-[#10B981]/20 text-[#10B981] text-xs font-semibold rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Activo
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-[#EF4444]/20 text-[#EF4444] text-xs font-semibold rounded-full flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Inactivo
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-[#CCCCCC]">
                      <MapPin className="w-4 h-4 text-[#666666]" />
                      <span>{concesionario.direccion}</span>
                    </div>
                    {concesionario.ciudad && (
                      <div className="flex items-center gap-2 text-[#CCCCCC]">
                        <MapPin className="w-4 h-4 text-[#666666]" />
                        <span>{concesionario.ciudad}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[#CCCCCC]">
                      <Phone className="w-4 h-4 text-[#666666]" />
                      <span>{concesionario.telefono}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#CCCCCC]">
                      <Mail className="w-4 h-4 text-[#666666]" />
                      <span>{concesionario.email}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleOpenModal(concesionario)}
                    className="p-2 text-[#F5A623] hover:bg-[#F5A623]/10 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(concesionario.id)}
                    className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Modal para crear/editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#2A2A2A] border border-[#F5A623]/30 rounded-2xl p-8 max-w-2xl">
          <DialogTitle className="text-2xl font-bold text-white mb-2">
            {editingId ? 'Editar Concesionario' : 'Nuevo Concesionario'}
          </DialogTitle>
          <DialogDescription className="text-[#666666] mb-6">
            {editingId 
              ? 'Actualiza la información del concesionario' 
              : 'Completa los datos del nuevo concesionario'}
          </DialogDescription>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre" className="text-white text-sm mb-2 block">
                Nombre del Concesionario *
              </Label>
              <Input
                id="nombre"
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                className="bg-[#1A1A1A] border-white/20 text-white"
                placeholder="Ej: Motos Caracas"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-white text-sm mb-2 block">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-[#1A1A1A] border-white/20 text-white"
                  placeholder="concesionario@example.com"
                />
              </div>

              <div>
                <Label htmlFor="telefono" className="text-white text-sm mb-2 block">
                  Teléfono *
                </Label>
                <Input
                  id="telefono"
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  required
                  className="bg-[#1A1A1A] border-white/20 text-white"
                  placeholder="0212-1234567"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="direccion" className="text-white text-sm mb-2 block">
                Dirección *
              </Label>
              <Input
                id="direccion"
                type="text"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                required
                className="bg-[#1A1A1A] border-white/20 text-white"
                placeholder="Av. Principal, Local 123"
              />
            </div>

            <div>
              <Label htmlFor="ciudad" className="text-white text-sm mb-2 block">
                Ciudad
              </Label>
              <Input
                id="ciudad"
                type="text"
                value={formData.ciudad}
                onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                className="bg-[#1A1A1A] border-white/20 text-white"
                placeholder="Caracas"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                id="activo"
                type="checkbox"
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                className="w-4 h-4 text-[#F5A623] bg-[#1A1A1A] border-white/20 rounded focus:ring-[#F5A623] focus:ring-2"
              />
              <Label htmlFor="activo" className="text-white text-sm cursor-pointer">
                Concesionario activo
              </Label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleCloseModal}
                className="flex-1 px-6 py-3 bg-[#3A3A3A] text-white rounded-lg hover:bg-[#4A4A4A] transition-colors font-semibold"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-[#fbb040] to-[#ffa500] text-black rounded-lg hover:shadow-lg hover:shadow-[#fbb040]/50 transition-all font-semibold disabled:opacity-50"
              >
                {submitting ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
